import {
  ContactTab,
  CurriculumTab,
  GeneralTab,
  PhotosTab,
  ScheduleTab
} from '@app/components'
import { Paper, Tabs } from '@mantine/core'
import {
  IconAddressBook,
  IconCalendarClock,
  IconInfoCircle,
  IconPhoto,
  IconSchool
} from '@tabler/icons-react'
import { useRouter } from 'next/router'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

const TAB_VALUES = ['general', 'contact', 'curriculum', 'schedule', 'photos'] as const
type TabValue = (typeof TAB_VALUES)[number]
const DEFAULT_TAB: TabValue = 'general'

const isTabValue = (value: unknown): value is TabValue =>
  typeof value === 'string' && (TAB_VALUES as readonly string[]).includes(value)

export const ProviderProfileTabs: FC = () => {
  const router = useRouter()
  const { query: routerQuery, replace: routerReplace, pathname } = router

  const activeTab = useMemo<TabValue>(
    () => (isTabValue(routerQuery.tab) ? routerQuery.tab : DEFAULT_TAB),
    [routerQuery.tab]
  )

  const [visitedTabs, setVisitedTabs] = useState<Record<TabValue, boolean>>(() => ({
    general: activeTab === 'general',
    contact: activeTab === 'contact',
    curriculum: activeTab === 'curriculum',
    schedule: activeTab === 'schedule',
    photos: activeTab === 'photos'
  }))
  useEffect(() => {
    setVisitedTabs((prev) => (prev[activeTab] ? prev : { ...prev, [activeTab]: true }))
  }, [activeTab])

  useEffect(() => {
    if (routerQuery.tab === activeTab) return
    void routerReplace({ pathname, query: { ...routerQuery, tab: activeTab } }, undefined, { shallow: true })
  }, [activeTab, pathname, routerQuery, routerReplace])

  const setTab = useCallback(
    (next: string | null) => {
      if (!isTabValue(next) || next === activeTab) return
      void routerReplace({ pathname, query: { ...routerQuery, tab: next } }, undefined, { shallow: true })
    },
    [activeTab, pathname, routerQuery, routerReplace]
  )

  return (
    <Paper p='lg'>
      <Tabs color='brand-red' onTabChange={setTab} value={activeTab}>
        <Tabs.List mb='lg'>
          <Tabs.Tab icon={<IconInfoCircle size={16} />} value='general'>General</Tabs.Tab>
          <Tabs.Tab icon={<IconAddressBook size={16} />} value='contact'>Contact</Tabs.Tab>
          <Tabs.Tab icon={<IconSchool size={16} />} value='curriculum'>Curriculum</Tabs.Tab>
          <Tabs.Tab icon={<IconCalendarClock size={16} />} value='schedule'>Schedule</Tabs.Tab>
          <Tabs.Tab icon={<IconPhoto size={16} />} value='photos'>Photos</Tabs.Tab>
        </Tabs.List>

        {visitedTabs.general && <Tabs.Panel value='general'><GeneralTab /></Tabs.Panel>}
        {visitedTabs.contact && <Tabs.Panel value='contact'><ContactTab /></Tabs.Panel>}
        {visitedTabs.curriculum && <Tabs.Panel value='curriculum'><CurriculumTab /></Tabs.Panel>}
        {visitedTabs.schedule && <Tabs.Panel value='schedule'><ScheduleTab /></Tabs.Panel>}
        {visitedTabs.photos && <Tabs.Panel value='photos'><PhotosTab /></Tabs.Panel>}
      </Tabs>
    </Paper>
  )
}
